import React, { useState, useMemo } from 'react';
import { Formik, Field, Form } from 'formik';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    IconButton,
    Snackbar,
    Alert,
    InputAdornment,
} from "@mui/material";
import { object, string } from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business'; 
import DescriptionIcon from '@mui/icons-material/Description'; 

function convertToUrlFriendlyString(input) {
    return input
        .toLowerCase()
        .replace(/[^\w\s-]|_/g, "")
        .replace(/\s+/g, "-")
        .replace(/^[^a-z0-9]+|[^\da-z0-9]+$/g, "");
}

const CreateOrganizationDialog = ({ onCreate, open }) => { 
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const handleCloseDialog = () => {
        console.log("Closing dialog");
    };

    const handleFormSubmit = (values, { setSubmitting, resetForm }) => {
        if (onCreate) {
            onCreate({
                name: convertToUrlFriendlyString(values.descriptiveName),
                descriptiveName: values.descriptiveName,
                description: values.description
            });
        }
        setSubmitting(false);
        
        setSnackbarMessage(`Organization "${values.descriptiveName}" submission processed.`);
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        handleCloseDialog();
        resetForm();
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    return (
        <>
            <Dialog
                fullWidth
                maxWidth="sm"
                open={open} 
                onClose={handleCloseDialog} 
                PaperProps={{
                    sx: {
                        borderRadius: '24px',
                        bgcolor: 'background.paper' 
                    }
                }}
            >
                <DialogTitle sx={{ pt: 3, pb: 2, px: 3.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img src="/ct_logo_black.png" alt="Candidate Tools Logo" className="logo-light" style={{ height: '28px', marginRight: '12px' }} />
                            <img src="/ct_logo.png" alt="Candidate Tools Logo" className="logo-dark" style={{ height: '28px', marginRight: '12px' }} />
                            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                                Create New Organization
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={handleCloseDialog}
                            sx={{ color: "text.secondary" }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                
                <Formik
                    initialValues={{ descriptiveName: "", description: "" }}
                    validationSchema={object({
                        descriptiveName: string().trim('Name cannot be only spaces').min(5, 'Name should be at least 5 characters').required('Name is required'),
                        description: string().trim('Description cannot be only spaces').min(5, 'Description should be at least 5 characters').required('Description is required'),
                    })}
                    onSubmit={handleFormSubmit}
                >
                    {({ errors, touched, isSubmitting }) => (
                        <Form>
                            <DialogContent sx={{ px: 3.5, py: 3.5 }}>
                                <Box mb={3.5}>
                                    <Field
                                        as={TextField}
                                        fullWidth
                                        required
                                        id="descriptiveName"
                                        name="descriptiveName"
                                        label="Organization Name"
                                        variant="filled"
                                        helperText={touched.descriptiveName ? errors.descriptiveName : "e.g., QuantumLeap Inc., Project Nova"}
                                        error={touched.descriptiveName && Boolean(errors.descriptiveName)}
                                        InputLabelProps={{ sx: { fontSize: '1rem' } }}
                                        InputProps={{
                                            startAdornment: ( <InputAdornment position="start"> <BusinessIcon sx={{ color: "text.secondary", fontSize: '1.25rem' }} /> </InputAdornment> ),
                                        }}
                                    />
                                </Box>
                                <Box>
                                    <Field
                                        as={TextField}
                                        fullWidth
                                        required
                                        multiline
                                        rows={4}
                                        id="description"
                                        name="description"
                                        label="Organization Description"
                                        variant="filled"
                                        helperText={touched.description ? errors.description : "Briefly outline its purpose, goals, or main activities."}
                                        error={touched.description && Boolean(errors.description)}
                                        InputLabelProps={{ sx: { fontSize: '1rem' } }}
                                        InputProps={{
                                            startAdornment: ( <InputAdornment position="start" sx={{mt: -7.5, alignItems: 'flex-start'}}> <DescriptionIcon sx={{ color: "text.secondary", fontSize: '1.25rem' }} /> </InputAdornment> ),
                                        }}
                                    />
                                </Box>
                            </DialogContent>
                            <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
                                <Button onClick={handleCloseDialog} sx={{ color: "text.primary", textTransform: 'none', fontWeight: 500 }}>
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={isSubmitting}
                                    sx={{ textTransform: 'none', fontWeight: 600, padding: '10px 28px', borderRadius: '12px' }}
                                >
                                    {isSubmitting ? "Creating..." : "Create Organization"}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    variant={"standard"}
                    sx={{ width: '100%', boxShadow: 6 }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default CreateOrganizationDialog;
